import Container from "components/general/Container";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import Seo from "components/general/Seo";
import Pagination from "components/navigation/Pagination";
import { Box, H3, HStack, Pressable, ScrollView, Text, Typography } from "components/ui";
import React, { useEffect } from "react";
import { ActivityIndicator } from "react-native";
import { useGetOrderQuery } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import OrdersHeader from "./OrdersHeader";
import OrdersTable from "./OrdersTable";
import { useToken } from "@gluestack-style/react";
import { useLocalSearchParams } from "expo-router";

type Props = {
	showPagination?: boolean | undefined | null;
	isDashboard?: boolean;
};

export const OrdersTabPage = ({ showPagination = true, isDashboard = false }: Props) => {
	const resolvedNeutral100 = useToken("colors", "neutral35");
	const { startDateQuery, endDateQuery, orderTypeQuery, statusQuery } = useLocalSearchParams<{
		startDateQuery: string;
		endDateQuery: string;
		orderTypeQuery: string;
		statusQuery: string;
	}>();
	const [pageNumber, setPageNumber] = React.useState(1);
	const [searchText, setSearchText] = React.useState("");
	const isMobile = useIsMobile();
	const [groupIdCollection, setGroupIdCollection] = React.useState<number[]>([]);
	const [userIdCollection, setUserIdCollection] = React.useState<number[]>([]);
	const [orderType, setOrderType] = React.useState<string>("");
	const [isAwaitingApproval, setIsAwaitingApproval] = React.useState(false);
	const [activeStatus, setActiveStatus] = React.useState<string>("");

	// Create default dates using useMemo to prevent recreation on every render
	const { threeMonthsAgo, today } = React.useMemo(() => {
		const threeMonthsBack = new Date();
		threeMonthsBack.setMonth(threeMonthsBack.getMonth() - 3);
		threeMonthsBack.setHours(0, 0, 0, 0);

		const todayDate = new Date();
		todayDate.setHours(23, 59, 59, 999);

		return { threeMonthsAgo: threeMonthsBack, today: todayDate };
	}, []);

	const [startDate, setStartDate] = React.useState<Date | null>(threeMonthsAgo);
	const [endDate, setEndDate] = React.useState<Date | null>(today);

	// Determine initial dates - use URL params if available, otherwise use defaults
	const initialStartDate = startDateQuery ? new Date(startDateQuery) : threeMonthsAgo;
	const initialEndDate = endDateQuery ? new Date(endDateQuery) : today;

	// Determine initial order type - use URL param if available
	const initialOrderType = orderTypeQuery || "";

	useEffect(() => {
		if (statusQuery) {
			setActiveStatus(statusQuery);
		}
	}, [statusQuery]);

	useEffect(() => {
		if (startDateQuery || endDateQuery || orderTypeQuery) {
			// URL params exist - use them
			if (startDateQuery) {
				setStartDate(new Date(startDateQuery));
				if (!endDateQuery) {
					setEndDate(today);
				}
			}
			if (endDateQuery) {
				setEndDate(new Date(endDateQuery));
			}
			if (orderTypeQuery) {
				setOrderType(orderTypeQuery);
			}
		} else {
			// No URL params - use default
			setStartDate(threeMonthsAgo);
			setEndDate(today);
			setOrderType("");
		}
	}, [startDateQuery, endDateQuery, orderTypeQuery]);

	const { data, isLoading, isFetching } = useGetOrderQuery({
		pageNumber,
		searchTerm: searchText,
		statusCollection: activeStatus ? [activeStatus] : undefined,
		groupIdCollection: groupIdCollection,
		userIdCollection: userIdCollection,
		startDate: startDateQuery ?? startDate?.toUTCString(),
		endDate: endDateQuery ?? endDate?.toUTCString(),
		orderType: orderTypeQuery ?? orderType ?? undefined,
		isAwaitingApproval: isAwaitingApproval,
	});

	const { data: notSent } = useGetOrderQuery({
		pageSize: 1,
		pageNumber: 1,
		statusCollection: ["Not Sent"],
		searchTerm: searchText,
		startDate: startDateQuery ?? startDate?.toUTCString(),
	});

	const { data: approvals } = useGetOrderQuery({
		pageSize: 1,
		pageNumber: 1,
		statusCollection: ["Awaiting Approval"],
		searchTerm: searchText,
		startDate: endDateQuery ?? endDate?.toUTCString(),
	});

	const handleOnSiteChange = (site: any[]) => {
		setGroupIdCollection(site);
	};

	const handleOnDateChange = (dates: any) => {
		setStartDate(dates.startDate);
		setEndDate(dates.endDate);
	};

	let allTabNotificationCount = 0;
	if (notSent && approvals) {
		allTabNotificationCount = notSent?.metaData.totalItemCount + approvals?.metaData.totalItemCount;
	}

	const TabCollection = [
		{ title: "All", status: "", notificationCount: allTabNotificationCount },
		{ title: "Not submitted", status: "Not Sent", notificationCount: notSent?.metaData.totalItemCount },
		{ title: "Approvals", status: "Awaiting Approval", notificationCount: approvals?.metaData.totalItemCount },
		{ title: "In transit", status: "In Transit" },
		{ title: "Partially delivered", status: "Partially Delivered" },
		{ title: "Delivered", status: "Complete" },
	];

	const handleTabClick = (tabStatus: string) => {
		setActiveStatus(tabStatus);
	};

	const OrderLink = (data: any) => {
		const isActiveTab = data.tabData.status === activeStatus;
		const textColour = isActiveTab ? "$primarylight" : "$neutral600";
		const fontWeight = isActiveTab ? 700 : 400;
		const borderBottom = isActiveTab ? 2 : 0;

		return (
			<Pressable onPress={() => handleTabClick(data.tabData.status)} style={{ flex: 1 }}>
				<Box
					sx={{
						backgroundColor: "$basicwhite",
						paddingVertical: 12,
						paddingHorizontal: isMobile ? 16 : 12,
						marginRight: 10,
						borderBottomColor: "$primarylight",
						borderBottomWidth: borderBottom,
					}}
					position="relative"
				>
					<HStack gap={8} alignItems="center" justifyContent="center">
						<Text sx={{ color: textColour, fontWeight: fontWeight, textAlign: "center" }}>{data.tabData.title}</Text>
						{data.tabData.notificationCount > 0 && (
							<Box justifyContent="center" alignItems="center" bg="$errormain" width={24} height={24} borderRadius={"$full"}>
								<Text color="$basicwhite">{data.tabData.notificationCount}</Text>
							</Box>
						)}
					</HStack>
				</Box>
			</Pressable>
		);
	};

	if (isMobile) {
		return (
			<ScrollContainer style={{ paddingTop: isMobile ? 0 : isDashboard ? 0 : 200 }} withFooter={!isDashboard}>
				<OrdersHeader
					onSiteChange={handleOnSiteChange}
					onOrderByChange={setUserIdCollection}
					onDateChange={handleOnDateChange}
					onOrderTypeChange={setOrderType}
					onSearchFilterChange={setSearchText}
					onShowAwaitingApproval={setIsAwaitingApproval}
					startDate={initialStartDate}
					endDate={initialEndDate}
					orderType={initialOrderType}
					isDashboard={isDashboard}
				/>

				<Section bg="$neutral25" gap={24} paddingHorizontal={isMobile ? 0 : isDashboard ? 0 : 47}>
					<ScrollView horizontal>
						<HStack paddingHorizontal={16} mb={12}>
							{TabCollection.map((child: any, index: number) => {
								return <OrderLink key={index} tabData={child} />;
							})}
						</HStack>
					</ScrollView>

					<Section mb={24} paddingVertical={isMobile ? 0 : isDashboard ? 0 : 47} paddingHorizontal={12} gap={24}>
						{isLoading || isFetching ? (
							<Box minHeight={400} justifyContent="center" alignItems="center" padding={20}>
								<ActivityIndicator size="large" color={"#1A428A"} style={{ transform: [{ scale: 1.5 }] }} />
							</Box>
						) : !data || !data.list || data.list.length === 0 ? (
							<Box flex={2} gap={12}>
								<H3>No Orders found.</H3>
								<Typography sx={{ lineHeight: 21 }}>This page keeps track of all your orders.</Typography>
								<Typography sx={{ lineHeight: 21 }}>Right now, it's empty — once orders are added, everything will appear here automatically.</Typography>
							</Box>
						) : (
							<>
								<OrdersTable isDashboard={isDashboard} status={activeStatus} dataList={data.list} />
								<Box justifyContent="center" marginTop={30} alignItems="center">
									{showPagination && <Pagination pageCount={data?.metaData?.pageCount} pageNumber={data?.metaData?.pageNumber} onChangePage={setPageNumber} />}
								</Box>
							</>
						)}
					</Section>
				</Section>
			</ScrollContainer>
		);
	}
	return (
		<ScrollContainer style={{ paddingTop: isMobile ? 0 : isDashboard ? 0 : 200 }} withFooter={!isDashboard}>
			<Seo title="Orders | Abconet" description="Track current, past, and pending orders placed through the Abconet platform." />
			<Container sx={{ marginBottom: 24, gap: 24 }}>
				<Box zIndex={99}>
					<OrdersHeader
						onSiteChange={handleOnSiteChange}
						onOrderByChange={setUserIdCollection}
						onDateChange={handleOnDateChange}
						onOrderTypeChange={setOrderType}
						onSearchFilterChange={setSearchText}
						onShowAwaitingApproval={setIsAwaitingApproval}
						startDate={initialStartDate}
						endDate={initialEndDate}
						orderType={initialOrderType}
						isDashboard={isDashboard}
					/>
				</Box>
				<Section zIndex={9} bg="$neutral25" gap={24} paddingHorizontal={isDashboard ? 0 : 47}>
					<Box flexDirection="row" mb={12}>
						{TabCollection.map((child: any, index: number) => {
							return <OrderLink key={index} tabData={child} />;
						})}
					</Box>

					{isLoading || isFetching ? (
						<Box minHeight={400} justifyContent="center" alignItems="center" padding={20}>
							<ActivityIndicator size="large" color={"#1A428A"} style={{ transform: [{ scale: 1.5 }] }} />
						</Box>
					) : !data || !data.list || data.list.length === 0 ? (
						<Box flex={2} gap={12}>
							<H3>No Orders found.</H3>
							<Typography sx={{ lineHeight: 21 }}>This page keeps track of all your orders.</Typography>
							<Typography sx={{ lineHeight: 21 }}>Right now, it's empty — once orders are added, everything will appear here automatically.</Typography>
						</Box>
					) : (
						<>
							<OrdersTable isDashboard={isDashboard} status={activeStatus} dataList={data.list} />
							<Box justifyContent="center" marginTop={30} alignItems="center">
								{showPagination && <Pagination pageCount={data?.metaData?.pageCount} pageNumber={data?.metaData?.pageNumber} onChangePage={setPageNumber} />}
							</Box>
						</>
					)}
				</Section>
			</Container>
		</ScrollContainer>
	);
};
